import React from 'react';
import { default as moment, Moment } from 'moment';
import {
    Card,
    CardActionArea,
    CardContent,
    Grid2 as Grid,
    ListItemText,
    Menu,
    List,
    Box,
    ListItemButton,
    CardActionAreaProps,
} from '@mui/material';
import { DateRangePicker } from './dateRangePicker';
import { ArrowDropDown, DateRange as DateRangeIcon } from '@mui/icons-material';
import { styled } from '@mui/styles';
import { DefinedRange, DateRange } from './dateRangePicker/types';

interface Props {
    readonly styled: StyledProps;
    readonly onFilter: (start: Moment, end: Moment) => void;
    readonly start: Moment;
    readonly end: Moment;
    readonly definedRanges?: DefinedRange[];
    readonly ignoreToday?: boolean;
}

interface State {
    readonly anchorEl: HTMLElement | undefined;
}

export class FilterDates extends React.Component<Props, State> {
    state: State = {
        anchorEl: undefined,
    };

    render() {
        const { onFilter, start, end, ignoreToday } = this.props;
        const { anchorEl } = this.state;
        const handleMenu = (event: any) => this.setState({ anchorEl: event.currentTarget });
        const closeMenu = () => this.setState({ anchorEl: undefined });

        const onCancelButton = () => {
            closeMenu();
        };

        let now = moment.parseZone().set('seconds', 0).set('millisecond', 0);
        if (ignoreToday) {
            now = now.subtract(1, 'days');
        }

        let { definedRanges } = this.props;
        if (!definedRanges) {
            definedRanges = [
                {
                    startDate: now.clone().subtract(60, 'minutes').toDate(),
                    endDate: now.clone().toDate(),
                    label: 'Last 60 minutes',
                },
                {
                    startDate: now.clone().subtract(24, 'hours').toDate(),
                    endDate: now.clone().toDate(),
                    label: 'Last 24 hours',
                },
                {
                    startDate: now.clone().subtract(6, 'days').toDate(),
                    endDate: now.clone().toDate(),
                    label: 'Last 7 days',
                },
                {
                    startDate: now.clone().subtract(1, 'months').toDate(),
                    endDate: now.clone().toDate(),
                    label: 'Last Month',
                },
                {
                    startDate: now.clone().subtract(2, 'months').toDate(),
                    endDate: now.clone().toDate(),
                    label: 'Last 2 Months',
                },
                {
                    startDate: now.clone().subtract(6, 'months').toDate(),
                    endDate: now.clone().toDate(),
                    label: 'Last 6 Months',
                },
            ];
        }

        const open = !!anchorEl;

        const setDateRange = (dateRange: DateRange) => {
            let startDate = moment(dateRange.startDate);
            let endDate = moment(dateRange.endDate);
            if (startDate.diff(endDate) === 0) {
                startDate = startDate.startOf('day');
                endDate = endDate.endOf('day');
            }
            closeMenu();
            onFilter(startDate, endDate);
        };

        const toggle = () => closeMenu();
        const initialDateRange: DateRange = {
            startDate: start.clone().set('seconds', 0).set('millisecond', 0).toDate(),
            endDate: end.clone().set('seconds', 0).set('millisecond', 0).toDate(),
        };

        const selectedDateRange = definedRanges
            .find(r => {
                if(!initialDateRange.startDate || !initialDateRange.endDate) {
                    return false;
                }

                return r.startDate.getTime() === initialDateRange.startDate.getTime() &&
                r.endDate.getTime() === initialDateRange.endDate.getTime();
            });
        const label = selectedDateRange ? selectedDateRange.label : 'Custom';

        return (
            <>
                <CardStyled>
                    <CardActionAreaStyles styled={this.props.styled} onClick={handleMenu}>
                        <CardContentStyles>
                            <DateRangeIcon style={{ margin: `auto`, color: 'inherit'}}/>
                            <Grid container={true} spacing={0} style={{ margin: `auto`}}>
                                <Grid size={12} style={{ fontSize: 12, lineHeight: '16px', fontWeight: 600}}>
                                    {label}
                                </Grid>
                                <Grid size={12} style={{ fontSize: 12, lineHeight: '14px'}}>
                                    {`${start.format('ll')} - ${end.format('ll')}`}
                                </Grid>
                            </Grid>
                            <ArrowDropDown style={{ margin: `auto`, color: 'inherit'}}/>
                        </CardContentStyles>
                    </CardActionAreaStyles>
                </CardStyled>
                <MainMenu
                    id={`filter`}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    open={open}
                    onClose={onCancelButton}
                    style={{
                        marginTop: 2,
                    }}
                >

                    <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
                        <MainList>
                            {definedRanges.map((dateRange, index) => {
                                const selected = selectedDateRange ? selectedDateRange === dateRange: false;
                                const handleListItemClick = () => setDateRange(dateRange);
                                return (
                                    <MainListItem
                                        key={`date_range_list_item_${index}`}
                                        selected={selected}
                                        onClick={handleListItemClick}
                                    >
                                        <ListItemText primary={dateRange.label} />
                                    </MainListItem>
                                );
                            })}
                        </MainList>
                    </Box>
                    <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                        <DateRangePicker
                            open={true}
                            toggle={toggle}
                            onChange={setDateRange}
                            closeOnClickOutside={true}
                            initialDateRange={initialDateRange}
                            definedRanges={definedRanges}
                            maxDate={moment.parseZone().toDate()}
                            wrapperClassName={'generateClassName'}
                        />
                    </Box>
                </MainMenu>
            </>
        );
    }
}

const MainMenu = styled(Menu)(({
    '& .MuiMenu-list': {
        padding: 0,
    },
}));

const CardStyled = styled(Card)(({
    '&.MuiCard-root': {
        margin: 0,
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        maxWidth: 230,
        color: 'inherit',
    },
}));

const CardContentStyles = styled(CardContent)(({
    '&.MuiCardContent-root': {
        padding: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        gridColumnGap: 10,
        color: 'inherit',
    },
}));

interface StyledCardActionAreaProps extends CardActionAreaProps {
    styled: StyledProps;
}
const CardActionAreaStyles = styled((props: StyledCardActionAreaProps) => (
    <CardActionArea
        {...props}
        sx={{
            '&.MuiCardActionArea-root': {
                "&$selected": {
                    backgroundColor: 'transparent',
                    color: props.styled.themeMode === 'light' ? '#7cacf8' : '#7cacf8',
                },
                "&:disabled": {
                    backgroundColor: props.styled.themeMode === 'light' ? '#fafafa' : '#0f1214',
                    color: 'rgb(0, 112, 201)',
                },
                "&:hover": {
                    backgroundColor: props.styled.themeMode === 'light' ? '#fafafa' : '#0f1214',
                    color: props.styled.themeMode === 'light' ? '#7cacf8' : '#7cacf8',
                },
            },
        }}
    />
))(() => ({}));

const MainList = styled(List)(({
    '&.MuiList-root': {
        minWidth: 200,
        padding: '14px 0',
    },
}));

const MainListItem = styled(ListItemButton)(({
    '&.MuiListItem-root': {
        padding: '0px 4px 0px 16px',
        height: 32,
        "&$selected": {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            "&:disabled": {
                backgroundColor: 'rgba(0, 0, 0, 0.08)'
            },
            "&:hover": {
                backgroundColor: 'rgba(0, 0, 0, 0.08)'
            },
        }
    },
    '&.MuiListItem-selected': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        '&:disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
    },
    padding: 0,
}));
